import { useState } from 'react';
import { useAdvanceStage } from '../lib/mutations';
import { Button, StatusBadge } from '../components/ui';
import { cn, formatScheduleDate, todayISO } from '../lib/utils';
import type { JobWithContext, JobStageWithStage, StageStatus } from '../lib/database.types';

interface StageCardProps {
    job: JobWithContext;
    js: JobStageWithStage;
    onOpenWeatherHold: () => void;
}

/** Pull the "FW Ready: YYYY-MM-DD" date Andrea stores in the notes field. */
function extractFwReady(notes: string | null): string | null {
    if (!notes) return null;
    const m = notes.match(/FW Ready:\s*(\d{4}-\d{2}-\d{2})/i);
    return m ? m[1] : null;
}

export function StageCard({ job, js, onOpenWeatherHold }: StageCardProps) {
    const advanceMut = useAdvanceStage();
    const [error, setError] = useState<string | null>(null);

    async function advanceTo(newStatus: StageStatus) {
        setError(null);
        try {
            await advanceMut.mutateAsync({
                p_job_stage_id: js.id,
                p_new_status: newStatus,
                p_completion_date: newStatus === 'complete' ? todayISO() : null,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        }
    }

    const nextActions = nextActionsFor(js.status);
    const fwReady = extractFwReady(job.notes);

    return (
        <div
            className={cn(
                'border rounded bg-white dark:bg-graphite-900 px-4 py-3.5',
                js.status === 'on_hold'
                    ? 'border-hold-500/40 pattern-hold'
                    : 'border-graphite-200 dark:border-graphite-800',
                js.status === 'complete' && 'opacity-70'
            )}
        >
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="font-mono text-[11px] uppercase tracking-wider text-graphite-500">
                            {job.builder.prefix}
                        </span>
                        <span className="font-display font-semibold text-graphite-900 dark:text-graphite-100 truncate">
                            {job.community.name}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-graphite-600 dark:text-graphite-400">
                        <span className="font-mono tabular text-graphite-800 dark:text-graphite-200">Lot {job.lot_number}</span>
                        {job.address ? <span className="text-graphite-500 truncate">· {job.address}</span> : null}
                    </div>
                </div>
                <StatusBadge status={js.status} size="sm" />
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-graphite-200 dark:border-graphite-800 mb-3">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-graphite-500">
                        Stage
                    </div>
                    <div className="font-display font-semibold text-graphite-900 dark:text-graphite-100 text-sm">
                        {js.stage.name}
                    </div>
                </div>
                <div className="ml-auto text-right">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-graphite-500">
                        Date
                    </div>
                    <div className="font-mono text-sm text-graphite-900 dark:text-graphite-100 tabular">
                        {formatScheduleDate(js.scheduled_date)}
                    </div>
                </div>
                {js.crew ? (
                    <div className="text-right">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-graphite-500">
                            Crew
                        </div>
                        <div className="font-mono text-sm text-signal-700 dark:text-signal-400">{js.crew.name}</div>
                    </div>
                ) : null}
            </div>

            {/* Flatwork ready date, pulled from the lot's notes */}
            {fwReady ? (
                <div className="flex items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-wider text-graphite-500">
                    <span>FW Ready</span>
                    <span className="text-graphite-800 dark:text-graphite-200 tabular normal-case">
                        {formatScheduleDate(fwReady)}
                    </span>
                </div>
            ) : null}

            {/* Contact - one-tap call on mobile */}
            {job.contact_phone ? (
                <div className="flex items-center gap-2 mb-3 text-sm">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-500">
                        Super
                    </span>
                    <span className="text-graphite-800 dark:text-graphite-200">{job.contact_name ?? 'Contact'}</span>
                    <a
                        href={`tel:${job.contact_phone.replace(/[^\d+]/g, '')}`}
                        className="ml-auto text-signal-700 hover:text-signal-600 dark:text-signal-400 dark:hover:text-signal-300 font-mono tabular text-sm"
                    >
                        {job.contact_phone}
                    </a>
                </div>
            ) : null}

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
                {nextActions.map((action) => (
                    <Button
                        key={action.status}
                        variant={action.primary ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => advanceTo(action.status)}
                        disabled={advanceMut.isPending}
                    >
                        {action.label}
                    </Button>
                ))}
                {js.status !== 'on_hold' ? (
                    <Button variant="danger" size="sm" onClick={onOpenWeatherHold}>
                        Weather hold
                    </Button>
                ) : (
                    <Button variant="secondary" size="sm" onClick={onOpenWeatherHold}>
                        Clear hold
                    </Button>
                )}
            </div>

            {error ? (
                <div className="mt-3 border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                    {error}
                </div>
            ) : null}
        </div>
    );
}

function nextActionsFor(status: StageStatus): Array<{ status: StageStatus; label: string; primary: boolean }> {
    switch (status) {
        case 'pending':
        case 'scheduled':
            return [
                { status: 'in_progress', label: 'Start', primary: true },
                { status: 'complete', label: 'Complete', primary: false },
            ];
        case 'in_progress':
            return [{ status: 'complete', label: 'Complete', primary: true }];
        case 'on_hold':
            return [{ status: 'scheduled', label: 'Resume', primary: true }];
        case 'complete':
            return [];
    }
}
